import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { MinigameStateSnapshot } from '../../shared/events';
import { Player } from '../../types';
import MinigameCountdown from './MinigameCountdown.component';
import MinigameResults from './MinigameResults.component';
import ClosestNumber from './games/ClosestNumber.game';
import NumberQuestion from './games/NumberQuestion.game';
import PickASide from './games/PickASide.game';
import FastestTap from './games/FastestTap.game';
import TypeTheWord from './games/TypeTheWord.game';
import VoteBoost from './games/VoteBoost.game';
import TeamRedBlue from './games/TeamRedBlue.game';

interface Props {
  minigame: MinigameStateSnapshot;
  players: Player[];
  currentPlayerId: string;
  isAdmin: boolean;
  onSubmit: (answer: number | string) => void;
  onCloseResults: () => void;
}

const TimerBar: React.FC<{ deadline: number }> = ({ deadline }) => {
  const [progress, setProgress] = useState(1);

  useEffect(() => {
    const start = deadline - 10000;
    const update = () => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 1 - elapsed / 10000));
    };
    update();
    const id = setInterval(update, 100);
    return () => clearInterval(id);
  }, [deadline]);

  const color = progress > 0.4 ? '#4caf50' : progress > 0.2 ? '#ff9800' : '#f44336';

  return (
    <Box sx={{ width: '100%', height: 10, background: 'rgba(255,255,255,0.2)', borderRadius: 5 }}>
      <Box
        sx={{
          height: '100%', width: `${progress * 100}%`,
          background: color, borderRadius: 5,
          transition: 'width 0.1s linear, background 0.3s',
        }}
      />
    </Box>
  );
};

const MinigameOverlay: React.FC<Props> = ({
  minigame, players, currentPlayerId, isAdmin, onSubmit, onCloseResults,
}) => {
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { setSubmitted(false); }, [minigame.type]);

  const handleSubmit = (answer: number | string) => {
    if (submitted) return;
    setSubmitted(true);
    onSubmit(answer);
  };

  if (minigame.phase === 'countdown') {
    return <MinigameCountdown minigame={minigame} />;
  }

  if (minigame.phase === 'results') {
    return (
      <MinigameResults
        minigame={minigame}
        players={players}
        isAdmin={isAdmin}
        onClose={onCloseResults}
      />
    );
  }

  // Active phase
  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 3, px: 3,
      }}
    >
      {/* Timer + answer counter */}
      <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            Time left
          </Typography>
          <Typography sx={{ fontFamily: '"Comic Sans MS", cursive', color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            {minigame.answeredCount}/{minigame.totalCount} answered
          </Typography>
        </Box>
        <TimerBar deadline={minigame.deadline} />
      </Box>

      {/* Game component */}
      {minigame.type === 'closest-number' && (
        <ClosestNumber submitted={submitted} onSubmit={handleSubmit} />
      )}
      {minigame.type === 'number-question' && (
        <NumberQuestion minigame={minigame} submitted={submitted} onSubmit={handleSubmit} />
      )}
      {minigame.type === 'pick-a-side' && (
        <PickASide minigame={minigame} submitted={submitted} onSubmit={handleSubmit} />
      )}
      {minigame.type === 'fastest-tap' && (
        <FastestTap minigame={minigame} submitted={submitted} onSubmit={handleSubmit} />
      )}
      {minigame.type === 'type-the-word' && (
        <TypeTheWord minigame={minigame} submitted={submitted} onSubmit={handleSubmit} />
      )}
      {minigame.type === 'vote-boost' && (
        <VoteBoost
          players={players}
          currentPlayerId={currentPlayerId}
          submitted={submitted}
          onSubmit={handleSubmit}
        />
      )}
      {minigame.type === 'team-red-blue' && (
        <TeamRedBlue submitted={submitted} onSubmit={handleSubmit} />
      )}
    </Box>
  );
};

export default MinigameOverlay;
